import { ref } from 'vue'

import type { AbilitiesResponse, AbilityIdentity } from './types/contract'
import type { RbacController, RbacOptions, TransportStatus } from './types/runtime'

import { applyPackedRules, createEmptyAbility } from './ability'

const detectInitialOnline = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : true

const identityOf = (response: AbilitiesResponse): AbilityIdentity => ({
  tenantId: response.tenant_id,
  subjectId: response.subject.id
})

const sameIdentity = (a: AbilityIdentity, b: AbilityIdentity): boolean =>
  a.tenantId === b.tenantId && a.subjectId === b.subjectId

/**
 * Builds the reactive controller. Reactivity is explicit: every rule mutation
 * bumps `revision`, which the composables read inside `computed`, so `can()` and
 * `has()` re-evaluate whenever rules or connectivity change.
 */
export const createController = (options: RbacOptions): RbacController => {
  const ability = createEmptyAbility()
  const status = ref<TransportStatus>('idle')
  const version = ref<string | null>(null)
  const permissions = ref<ReadonlySet<string>>(new Set())
  const identity = ref<AbilityIdentity | null>(null)
  const meta = ref<AbilitiesResponse | null>(null)
  const online = ref<boolean>(detectInitialOnline())
  const revision = ref(0)

  const clear = (): void => {
    ability.update([])
    version.value = null
    permissions.value = new Set()
    identity.value = null
    meta.value = null
    revision.value += 1
  }

  const applyResponse = (response: AbilitiesResponse): void => {
    const previousVersion = version.value
    const previousIdentity = identity.value
    const nextIdentity = identityOf(response)

    // `tenant_id` + `subject` exist on the wire so a client can notice it is
    // holding somebody else's ability set. A tenant switch or a user switch in
    // the same tab is the case that matters: without this the previous
    // principal's rules survive until the next fetch, and the UI renders
    // affordances for a tenant the user is no longer in. Discarding first means
    // the new principal is built from empty — fail-closed — rather than layered
    // over the old one.
    const switchedFrom =
      previousIdentity !== null && !sameIdentity(previousIdentity, nextIdentity) ? previousIdentity : null

    if (switchedFrom) {
      clear()
      options.onIdentityChange?.(nextIdentity, switchedFrom)
    }

    applyPackedRules(ability, response.rules)
    version.value = response.version
    permissions.value = new Set(response.permissions)
    identity.value = nextIdentity
    meta.value = response
    revision.value += 1

    // Version drift means "the same principal's permissions changed". Across a
    // principal switch the two versions describe different people, so comparing
    // them says nothing — `onIdentityChange` is the honest signal there.
    if (switchedFrom === null && previousVersion !== null && previousVersion !== response.version) {
      options.onVersionDrift?.(response.version, previousVersion)
    }
  }

  const setStatus = (next: TransportStatus): void => {
    status.value = next
  }

  const refresh = (): Promise<void> => options.transport.refresh()

  return {
    ability,
    gates: options.gates ?? [],
    status,
    version,
    permissions,
    identity,
    meta,
    online,
    revision,
    applyResponse,
    clear,
    refresh,
    setStatus
  }
}
